import axios from "axios";
import {defineStore} from "pinia";
import {ref} from "vue";
import {handleError} from "./storeHelper";
import {NotificationEmailType, ScheduledItem} from "../components/Notices/types";
import {SelectItem} from "../types";
import {NoticeModel} from "../components/Notices/types";
import {useOriStore} from "./oriStore";
import {BTableColumn, BTableOptions} from "@/components/BTable/BTable.types";
import {useGlobalMessagesStore} from "./globalMessagesStore";

const oriStore = useOriStore();

interface emailRecipientDropDownItem {
    label: string;
    value: NotificationEmailType;
}

export const useScheduledNoticeStore = defineStore("scheduledNoticeStore", () => {
    const apiUrl = "api/v1/task";

    const globalMessages = useGlobalMessagesStore();

    const loading = ref<boolean>(false);
    var tableOptions = ref({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                key: "created_on",
                isDescending: true,
            },
        ],
    } as BTableOptions);

    const columns = [
        {key: "header", text: "Headline", sortable: false, value: (item) => item.header},
        {
            key: "firstRun",
            text: "First Run",
            sortable: false,
            value: (item) => item.first_run.replace("0001-01-01", ""),
        },
        {key: "lastRun", text: "Last Run", sortable: false, value: (item) => item.last_run.replace("0001-01-01", "")},
        {
            key: "nextRun",
            text: "Next Run",
            sortable: false,
            value: (item) => item.next_run.replace("0001-01-01", "").replace("9999-12-31", ""),
        },
        {key: "actions", text: "", sortable: false, value: (item) => item},
    ] as BTableColumn[];

    var scheduledNotices = ref([] as ScheduledItem[]);
    const conditions = ref<SelectItem[]>([]);
    const emailRecipients = ref<emailRecipientDropDownItem[]>([]);
    const repeatOptions = ref<SelectItem[]>([]);
    const availableReports = ref<SelectItem[]>([]);

    const getConditions = async () => {
        try {
            loading.value = true;
            let response = await axios.get<any[]>(`${apiUrl}/conditions`);
            if (response?.data) {
                conditions.value = response.data as SelectItem[];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getEmailRecipients = async () => {
        try {
            loading.value = true;
            let response = await axios.get<any[]>(`${apiUrl}/email-recipients`);
            if (response?.data) {
                emailRecipients.value = response.data as emailRecipientDropDownItem[];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getRepeatOptions = async () => {
        try {
            loading.value = true;
            let response = await axios.get<any[]>(`${apiUrl}/repeat-options`);

            if (response?.data) {
                repeatOptions.value = response.data as SelectItem[];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getScheduledNotices = async () => {
        try {
            loading.value = true;
            let response = await axios.get<ScheduledItem[]>(apiUrl);

            if (response?.data) {
                scheduledNotices.value = response.data as ScheduledItem[];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const createScheduledTask = async (notice: NoticeModel) => {
        try {
            loading.value = true;
            await axios.put(apiUrl, notice);
            globalMessages.push({message: "Notice created successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const editScheduledTask = async (id: Number, notice: NoticeModel) => {
        try {
            loading.value = true;
            await axios.post(`${apiUrl}/${id}`, notice);
            globalMessages.push({message: "Notice updated successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const deleteScheduledTask = async (id: Number) => {
        try {
            loading.value = true;
            await axios.delete(`${apiUrl}/${id}`);
            globalMessages.push({message: "Notice deleted successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getAvailableReports = async () => {
        try {
            loading.value = true;
            let response = await axios.get<any[]>(`${apiUrl}/available-reports`);

            if (response?.data) {
                availableReports.value = response.data as SelectItem[];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    return {
        loading,
        conditions,
        emailRecipients,
        repeatOptions,
        scheduledNotices,
        columns,
        tableOptions,
        availableReports,
        getScheduledNotices,
        getConditions,
        getEmailRecipients,
        getRepeatOptions,
        createScheduledTask,
        editScheduledTask,
        deleteScheduledTask,
        getAvailableReports
    };
});
